import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { ImageIcon } from '@sketchnote/icons';
import { Switch } from '@sketchnote/shared-components';
import cn from 'classnames';
import { Emoji } from 'emoji-mart';
import { useRouter } from 'next/router';
import * as React from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { CoverImage, getRandomCoverImage } from 'src/components/CoverImage';
import { useSidebarStore } from 'src/components/sidebar/store';
import { EmojiPopover } from 'src/features/EmojiPopover';
import { useFileStore } from 'src/features/files';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';

import { useFormsStore } from '../../../store/useFormsStore';

const { useCallback, useState, useEffect } = React;

interface HeaderProps {
  formId: string;
}

export const Header = ({ formId }: HeaderProps) => {
  const form = useFormsStore(useCallback((s) => s.forms[formId], [formId]));
  const { onChangeForm, fetchForm, onUpdateFormProperties } = useFormsStore((s) => ({
    onChangeForm: s.onChangeForm,
    fetchForm: s.fetchForm,
    onUpdateFormProperties: s.onUpdateFormProperties,
  }));

  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [icon, setIcon] = useState<any>();
  const fetchSidebar = useSidebarStore((s) => s.fetchSidebar);
  const router = useRouter();
  const [inputTxt, setInputTxt] = useState('');
  const [descriptionText, setDescriptionText] = useState('');
  const fileId = router.query.slug as string;
  const currentFile = useFileStore(useCallback((s) => s.files[fileId], [fileId]));
  const toggleEmojiPicker = (value?: boolean) => () => {
    setIsEmojiPickerOpen(value ?? !isEmojiPickerOpen);
  };

  const { isReadOnly } = useFileAccess();

  const fetchFile = useFileStore((s) => s.fetchFile);
  useEffect(() => {
    form?.title && setInputTxt(form?.title);
    setIcon(form?.icon);
    form?.description && setDescriptionText(form?.description);
    return () => setInputTxt('');
  }, [form?.title, form?.icon, form?.description, currentFile?.name]);

  useEffect(() => {
    currentFile?.name && setInputTxt(currentFile.name);
  }, [currentFile?.name]);

  if (!form) return <></>;

  const handleOnAddCover = async (coverImage: any) => {
    const response: any = await onUpdateFormProperties({
      id: formId,
      coverImage: typeof coverImage === 'string' ? coverImage : getRandomCoverImage(),
      showCover: true,
    });

    if (response?.data?.success) fetchForm(formId);
  };
  const handleOnDisableCover = async () => {
    const response: any = await onUpdateFormProperties({
      id: formId,
      showCover: false,
    });
  };

  const handleUpdateFields = async (values: {
    title?: string;
    icon?: any;
    description?: string;
  }) => {
    setIsEmojiPickerOpen(false);

    const response: any = await onUpdateFormProperties({
      id: formId,
      title: values.title,
      description: values.description,
      icon: values.icon,
    });

    if (response) {
      fetchFile({ id: fileId });
      if (currentFile?.pin) {
        fetchSidebar(router?.query?.mode as string);
      }
    }
  };

  return (
    <div className={cn('mb-24 border-b')}>
      {form?.showCover && (
        <CoverImage
          coverImage={form?.cover || ''}
          onChangeCover={(coverImage) => handleOnAddCover(coverImage)}
          onDisableCover={handleOnDisableCover}
        />
      )}

      <div className={cn('max-w-1180 px-32 mx-auto')}>
        <div className={cn('mt-24 mb-10')}>
          <div className="flex flex-row">
            {/* */}
            {!isReadOnly && !form.showCover && (
              <div className={cn('flex text-gray-500 pb-18')}>
                <button className="flex mr-16" onClick={handleOnAddCover}>
                  <ImageIcon className="text-gray-500 mr-4 mt-4 block" />
                  Add form cover
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center">
            {!isReadOnly && (
              <div>
                <Popover
                  offset={[180, 10]}
                  isOpen={isEmojiPickerOpen}
                  onClose={toggleEmojiPicker(false)}
                  onOpen={toggleEmojiPicker(true)}
                  modifiers={[{ name: 'eventListeners', options: { scroll: isEmojiPickerOpen } }]}
                >
                  <PopoverTrigger>
                    <button
                      disabled={isReadOnly}
                      className="border border-gray-100 shadow-md bg-gray-199 rounded-sm mr-12 flex items-center justify-center h-40 w-40"
                    >
                      {form?.icon?.type === 'image' ? (
                        <img className="w-25 h-25 m-auto" src={form?.icon?.id} />
                      ) : (
                        <Emoji
                          emoji={
                            (form?.icon?.id as any) || {
                              id: 'bulb',
                            }
                          }
                          set="apple"
                          size={25}
                        />
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent>
                    <EmojiPopover onChangeIcon={handleUpdateFields} />
                  </PopoverContent>
                </Popover>
              </div>
            )}
            <div className="flex w-full">
              <input
                placeholder="Untitled"
                value={inputTxt}
                className={cn('text-4xl font-semibold mr-12 bg-white w-full flex-grow')}
                onChange={(e) => setInputTxt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleUpdateFields({ title: inputTxt });
                }}
                onBlur={() => handleUpdateFields({ title: inputTxt })}
                disabled={isReadOnly}
              />

              <div className="flex items-center gap-8">
                <Switch
                  checked={!!form?.isActive}
                  onChange={() => {
                    onChangeForm('isActive', !form?.isActive, formId);
                  }}
                />
                <span className="text-xs text-gray-500">Active</span>
              </div>
            </div>
          </div>
          <p className={cn('font-regular text-xs text-gray-500 ml-52')}>
            Created by {currentFile?.createdBy}
          </p>
          <span className="text-xs text-gray-500 mt-12 ml-52">Description</span>
          <p>
            <TextareaAutosize
              className="bg-white w-4/5 overflow-auto ml-52"
              disabled={isReadOnly}
              placeholder="Click here to add description"
              value={descriptionText}
              onChange={(e) => setDescriptionText(e.target.value)}
              onBlur={() => handleUpdateFields({ description: descriptionText })}
              style={{ resize: 'none' }}
            />
          </p>
        </div>
      </div>
    </div>
  );
};
